import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from './base.api';
import {
	GoodYouDoProjectResponseDto,
	CreateGoodYouDoProjectDto,
	UpdateGoodYouDoProjectDto,
	UpdateGoodYouDoSequenceDto,
	normalizeGoodYouDoProjectFromApi,
	normalizeGoodYouDoProjectsFromApi
} from '../../models/good-you-do';

const baseUrl = `${process.env.REACT_APP_PROFILE_URL}/api/v2/profile`;

/**
 * Runs a project save, returning a failed request as `{ error: { status, data } }` rather than throwing it. A thrown
 * error reaches the form without its response, so the API's reason (e.g. the image limit, TC-2901) would be lost.
 */
const runProjectSave = async (profileId: number, send: () => unknown) => {
	try {
		const result = (await send()) as { data: GoodYouDoProjectResponseDto };
		return {
			data: normalizeGoodYouDoProjectFromApi(profileId, result.data) as GoodYouDoProjectResponseDto
		};
	} catch (error) {
		const response = (error as { response?: { status?: number; data?: unknown } }).response;
		return { error: { status: response?.status, data: response?.data } };
	}
};

export const goodYouDoApi = createApi({
	reducerPath: 'goodYouDoApi',
	baseQuery: axiosBaseQuery(baseUrl),
	tagTypes: ['goodYouDo'],
	endpoints: (builder) => ({
		getProjects: builder.query<GoodYouDoProjectResponseDto[], number>({
			query: (profileId) => ({
				url: `/${profileId}/goodyoudo`,
				method: 'GET'
			}),
			transformResponse: (response: GoodYouDoProjectResponseDto[], _meta, profileId) =>
				normalizeGoodYouDoProjectsFromApi(profileId, response),
			providesTags: (_result, _error, profileId) => [
				{ type: 'goodYouDo', id: profileId },
				{ type: 'goodYouDo', id: 'LIST' }
			]
		}),

		getProject: builder.query<
			GoodYouDoProjectResponseDto,
			{ profileId: number; projectId: number }
		>({
			query: ({ profileId, projectId }) => ({
				url: `/${profileId}/goodyoudo/${projectId}`,
				method: 'GET'
			}),
			transformResponse: (response: GoodYouDoProjectResponseDto, _meta, arg) =>
				normalizeGoodYouDoProjectFromApi(arg.profileId, response) as GoodYouDoProjectResponseDto,
			providesTags: (_result, _error, { profileId, projectId }) => [
				{ type: 'goodYouDo', id: profileId },
				{ type: 'goodYouDo', id: `PROJECT-${projectId}` }
			]
		}),

		createProject: builder.mutation<
			GoodYouDoProjectResponseDto,
			{ profileId: number; request: CreateGoodYouDoProjectDto }
		>({
			queryFn: ({ profileId, request }, _api, _extraOptions, baseQuery) =>
				runProjectSave(profileId, () =>
					baseQuery({ url: `/${profileId}/goodyoudo`, method: 'POST', data: request })
				),
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'goodYouDo', id: profileId },
				{ type: 'goodYouDo', id: 'LIST' }
			]
		}),

		updateProject: builder.mutation<
			GoodYouDoProjectResponseDto,
			{ profileId: number; request: UpdateGoodYouDoProjectDto }
		>({
			queryFn: ({ profileId, request }, _api, _extraOptions, baseQuery) =>
				runProjectSave(profileId, () =>
					baseQuery({ url: `/${profileId}/goodyoudo`, method: 'PUT', data: request })
				),
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'goodYouDo', id: profileId },
				{ type: 'goodYouDo', id: 'LIST' }
			]
		}),

		deleteProject: builder.mutation<
			boolean,
			{ profileId: number; projectId: number; isProfilePublished?: boolean }
		>({
			query: ({ profileId, projectId, isProfilePublished }) => ({
				url: `/${profileId}/goodyoudo/${projectId}`,
				method: 'DELETE',
				params: isProfilePublished ? { isProfilePublished } : undefined
			}),
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'goodYouDo', id: profileId },
				{ type: 'goodYouDo', id: 'LIST' }
			]
		}),

		updateProjectSequence: builder.mutation<
			void,
			{ profileId: number; projectId: number; request: UpdateGoodYouDoSequenceDto }
		>({
			query: ({ profileId, projectId, request }) => ({
				url: `/${profileId}/goodyoudo/${projectId}/sequence`,
				method: 'PUT',
				data: request
			}),
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'goodYouDo', id: profileId },
				{ type: 'goodYouDo', id: 'LIST' }
			]
		})
	})
});

export const {
	useGetProjectsQuery,
	useGetProjectQuery,
	useCreateProjectMutation,
	useUpdateProjectMutation,
	useDeleteProjectMutation,
	useUpdateProjectSequenceMutation
} = goodYouDoApi;

export default goodYouDoApi;
